import dayjs from 'dayjs';
import React, { forwardRef, useImperativeHandle } from 'react';
import { i18n } from 'src/common';
import { InputStreet, InputCity, Card } from 'src/components';
import { InputNumber } from 'src/components/inputs/inputNumber.input';
import { InputText } from 'src/components/inputs/inputText.input';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';

export interface ApartmentPropertyInfoFormData {
  area?: number;
  exactYear?: number;
  city?: string;
  cityKompareId?: string;
  street?: string;
  streetKompareId?: string;
  zipCode?: string;
  houseNumber?: string;
}

interface ApartmentPropertyInfoFormProps {
  data: ApartmentPropertyInfoFormData;
  onSubmit: (values: ApartmentPropertyInfoFormData) => void;
}

export const ApartmentPropertyInfoForm = forwardRef<FormRef, ApartmentPropertyInfoFormProps>(({
  data,
  onSubmit,
}, ref) => {
  const formRef = React.useRef<FormRef>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        formRef.current.submitForm();
      }
    },
    getValues: () => formRef.current?.getValues?.() || {},
    validateForm: () => formRef.current?.validateForm?.() || false,
    setFieldValue: (fieldName: string, value: any, validation?: boolean) => {
      formRef.current?.setFieldValue?.(fieldName, value, validation);
    }
  }));

  const fields: FieldConfig<ApartmentPropertyInfoFormData>[] = [
    {
      name: 'area',
      label: i18n.t('components.forms.apartmentPropertyInfo.area'),
      component: InputNumber,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.apartmentPropertyInfo.areaRequired')
        },
        {
          type: 'min',
          message: i18n.t('components.forms.apartmentPropertyInfo.areaMin'),
          params: { minValue: 1 }
        }
      ] as Validation[]
    },
    {
      name: 'exactYear',
      label: i18n.t('components.forms.apartmentPropertyInfo.exactYear'),
      component: InputNumber,
      componentProps: {
        max: dayjs().year()
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.apartmentPropertyInfo.exactYearRequired')
        }
      ] as Validation[]
    },
    {
      name: 'city',
      label: i18n.t('components.forms.apartmentPropertyInfo.city'),
      component: InputCity,
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.apartmentPropertyInfo.cityRequired')
        }
      ] as Validation[]
    },
    {
      name: 'street',
      label: i18n.t('components.forms.apartmentPropertyInfo.street'),
      component: InputStreet,
      componentProps: {
        cityKompareId: 'streetKompareId',
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.apartmentPropertyInfo.streetRequired')
        }
      ] as Validation[]
    },
    {
      name: 'houseNumber',
      label: i18n.t('components.forms.apartmentPropertyInfo.houseNumber'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.apartmentPropertyInfo.houseNumberRequired')
        }
      ] as Validation[]
    }
  ];

  const handleSubmit = (values: ApartmentPropertyInfoFormData) => {
    // Convert string values to numbers for area and exactYear
    const convertedValues: ApartmentPropertyInfoFormData = {
      ...values,
      area: typeof values.area === 'string' ? (values.area ? parseFloat(parseFloat(values.area).toFixed(2)) : undefined) : values.area,
      exactYear: typeof values.exactYear === 'string' ? (values.exactYear ? parseInt(values.exactYear, 10) : undefined) : values.exactYear,
    };
    onSubmit(convertedValues);
  };

  // const isFormValid = !!(data.area && data.exactYear && data.city && data.street && data.houseNumber);

  return (
    <Card style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: 24,
      paddingTop: 32,
    }}>
      <Form
        ref={formRef}
        fields={fields}
        initialValues={data}
        onSubmit={handleSubmit}
        config={{ gap: 24 }}
      />
    </Card>
  );
});
